"use client";

import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Link as ChakraLink,
  Container,
  Card,
  CardBody,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import PasswordStrengthBar from "./PasswordStrengthBar";
import { validatePassword, PASSWORD_MIN_LENGTH } from "../lib/passwordPolicy";

export default function SignUp() {
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Client-side validation
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      setError(passwordCheck.message || "Password does not meet requirements");
      setLoading(false);
      return;
    }

    try {
      console.log("[SignUp] Calling server signup endpoint");

      // Call server-side signup endpoint
      const response = await fetch(`${API_BASE_URL}/api/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          password,
          firstName,
          lastName,
          companyName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("[SignUp] Server error:", data);

        // Handle specific error cases
        if (response.status === 409) {
          // Email already registered
          toast({
            title: "Email already registered",
            description:
              data.message ||
              "This email is already registered. Please sign in instead.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          setError(
            data.message ||
              "This email is already registered. Please sign in instead.",
          );
        } else {
          const errorMessage =
            data.error ||
            data.message ||
            "An error occurred during signup. Please try again.";
          toast({
            title: "Signup failed",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          setError(errorMessage);
        }
        setLoading(false);
        return;
      }

      // Success - navigate to verification page
      console.log("[SignUp] Signup successful:", data);
      toast({
        title: "Account created successfully",
        description: "Please check your email to verify your account.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      navigate("/verify", { state: { email } });
    } catch (error: any) {
      console.error("[SignUp] Unexpected error:", error);
      toast({
        title: "Unexpected error",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Box
                as="img"
                src="/main-logo.png"
                alt="CallSupplai Logo"
                h="60px"
                mx="auto"
                mb={2}
              />

              <Text color="secondary.600" fontSize="sm">
                Get your{" "}
                <Text as="span" color="blue.600">
                  WhiteLabelled AI Voice Agent Platform
                </Text>
                . No credit card required to Sign Up.
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box
              as="form"
              onSubmit={handleSignUp}
              w="100%"
              autoComplete="on"
            >
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.600">First Name</FormLabel>
                  <Input
                    name="firstName"
                    autoComplete="given-name"
                    type="text"
                    placeholder="Your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Last Name</FormLabel>
                  <Input
                    name="lastName"
                    autoComplete="family-name"
                    type="text"
                    placeholder="Your last name"
                    value={lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLastName(e.target.value)
                    }
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Company Name</FormLabel>
                  <Input
                    name="companyName"
                    autoComplete="organization"
                    type="text"
                    placeholder="Your company name"
                    value={companyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCompanyName(e.target.value)
                    }
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Email</FormLabel>
                  <Input
                    name="email"
                    autoComplete="email"
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Password</FormLabel>
                  <InputGroup>
                    <Input
                      name="password"
                      autoComplete="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.target.value)
                      }
                      isDisabled={loading}
                      minLength={PASSWORD_MIN_LENGTH}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                      textFillColor={"gray.600"}
                      sx={{ caretColor: "black" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        isDisabled={loading}
                        variant="ghost"
                        size="sm"
                        _placeholder={{ color: "gray.500" }}
                        textFillColor={"gray.600"}
                        sx={{ caretColor: "black" }}
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <PasswordStrengthBar password={password} />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Confirm Password</FormLabel>
                  <InputGroup>
                    <Input
                      name="confirmPassword"
                      autoComplete="new-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setConfirmPassword(e.target.value)
                      }
                      isDisabled={loading}
                      minLength={PASSWORD_MIN_LENGTH}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                      textFillColor={"gray.600"}
                      sx={{ caretColor: "black" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        icon={
                          showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                        }
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        isDisabled={loading}
                        variant="ghost"
                        size="sm"
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  variant="brandGradient"
                  type="submit"
                  size="lg"
                  w="100%"
                  isLoading={loading}
                  loadingText="Creating Account..."
                >
                  Create Account
                </Button>
              </VStack>
            </Box>

            <Text fontSize="sm" color="gray.600">
              Already have an account?{" "}
              <ChakraLink as={Link} to="/login" color="primary.600">
                Sign In
              </ChakraLink>
            </Text>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
